import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Table,
  Button,
  Spinner,
  FormGroup,
  Label,
  Input,
  Badge,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  masterSimList,
  masterSimCompanies,
  masterSimData,
  masterSimCreate,
  masterSimUpdate,
  masterSimDelete,
  masterSimSync,
  masterSimRegister,
  masterSimActivate,
  masterSimSuspend,
  masterSimAssign,
  masterSimUnassign,
} from "../../../../store/actions";
import AppModal from "../../../../Components/Common/AppModal";
import {
  getMasterSimStaff,
  getMasterSimDeviceOptions,
  postMasterSimMakeLive,
} from "../../../../helpers/fakebackend_helper";
import SettingsPanel from "../../components/SettingsPanel";
import EsimQrModal from "./EsimQrModal";
import SimDetailModal from "./SimDetailModal";
import { statusColor, displayBic, simType } from "./simHelpers";

const initialRegisterForm = {
  Bic: "",
  Activate: false,
  Label: "",
  Company_Id: "",
  Staff_Id: "",
};

const initialFormState = {
  Label: "",
  Iccid: "",
  Bic: "",
  Imsi: "",
  Msisdn: "",
  Notes: "",
};

// emnify BIC: 16 characters, '#' included, entered into a ____-____-____-____
// mask like the emnify portal. State holds the raw characters only.
const BIC_LENGTH = 16;
const cleanBic = (value) =>
  value
    .toUpperCase()
    .replace(/[^#A-Z0-9]/g, "")
    .slice(0, BIC_LENGTH);
const maskBic = (raw) =>
  raw.padEnd(BIC_LENGTH, "_").replace(/(.{4})(?=.)/g, "$1-");
// Caret position just after the last typed character in the masked string
const bicCaret = (raw) =>
  raw.length === 0 ? 0 : raw.length + Math.floor((raw.length - 1) / 4);

const MasterSim = () => {
  const dispatch = useDispatch();

  const {
    masterSimList: listResponse,
    masterSimListLoading: isLoading,
    masterSimCompanies: companiesResponse,
    masterSimData: dataResponse,
    masterSimDataLoading: isHistoryLoading,
    masterSimSyncLoading: isSyncing,
    masterSimRegisterLoading: isRegistering,
  } = useSelector((state) => state.masterSimReducer);

  const sims = listResponse?.mastersim_list || [];
  const companies = companiesResponse?.company_list || [];
  const historySim = dataResponse?.mastersim_data || null;

  // Change #100662 - search the inventory. Every word must match somewhere in
  // the row (label, ICCID, BIC, IMSI, MSISDN, EID, status, device, company,
  // person), so "lenovo activated" narrows rather than widens.
  const [simSearch, setSimSearch] = useState("");
  const simSearchWords = simSearch
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  const filteredSims = simSearchWords.length
    ? sims.filter((item) => {
        const haystack = [
          item.Label,
          item.Iccid,
          item.Bic,
          item.Imsi,
          item.Msisdn,
          item.Eid,
          item.SimFormFactor,
          item.SimModelDescription,
          simType(item).kind === "esim" ? "esim" : "",
          simType(item).kind === "physical" ? "physical" : "",
          item.StatusDescription,
          item.EmnifyEndpointId,
          item.EmnifyEndpointId ? "" : "no device",
          item.CurrentCompanyName,
          item.CurrentCompany_Id ? "" : "in stock",
          item.CurrentStaffName,
          item.Notes,
        ]
          .filter((v) => v !== null && v !== undefined && v !== "")
          .join(" ")
          .toLowerCase();
        return simSearchWords.every((word) => haystack.includes(word));
      })
    : sims;

  // ------- local state -------
  const [formData, setFormData] = useState(initialFormState);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignSim, setAssignSim] = useState(null);
  const [assignForm, setAssignForm] = useState({
    Company_Id: "",
    Staff_Id: "",
    Notes: "",
  });
  // Change #100344: active staff of the company picked in the assign popup
  const [assignStaff, setAssignStaff] = useState([]);
  const [isStaffLoading, setIsStaffLoading] = useState(false);

  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  // Change #100343: register a SIM batch at emnify by its BIC
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [registerForm, setRegisterForm] = useState(initialRegisterForm);
  const bicInputRef = useRef(null);

  // Change #100416: "Make live" - activate the SIM AND put it on an enabled
  // emnify device (endpoint). Activating alone leaves the SIM with no device,
  // so it passes no data.
  const [liveSim, setLiveSim] = useState(null);
  const [liveOptions, setLiveOptions] = useState(null);
  const [liveForm, setLiveForm] = useState({});
  const [liveSaving, setLiveSaving] = useState(false);

  // Keep the caret at the end of what has been typed, never out in the
  // underscores, so typing fills the mask and Backspace removes a character.
  const keepBicCaret = () => {
    const input = bicInputRef.current;
    if (!input || input.selectionStart !== input.selectionEnd) return;
    const caret = bicCaret(registerForm.Bic);
    if (input.selectionStart > caret) {
      setTimeout(() => input.setSelectionRange(caret, caret), 0);
    }
  };

  useEffect(() => {
    const input = bicInputRef.current;
    if (!registerModalOpen || !input || document.activeElement !== input)
      return;
    const caret = bicCaret(registerForm.Bic);
    input.setSelectionRange(caret, caret);
  }, [registerForm.Bic, registerModalOpen]);

  // Change #100664 - the SIM whose eSIM QR code is open.
  const [esimQrSim, setEsimQrSim] = useState(null);
  // Change #100665 - the SIM open in the SIM modal. Held as an id so the modal
  // always shows the latest row after an action or a live refresh.
  const [detailSimId, setDetailSimId] = useState(null);
  const detailSim = detailSimId
    ? sims.find((s) => String(s.Id) === String(detailSimId)) || null
    : null;
  const [labelEditId, setLabelEditId] = useState(null);
  const [labelDraft, setLabelDraft] = useState("");

  useEffect(() => {
    dispatch(masterSimList());
    dispatch(masterSimCompanies());
  }, [dispatch]);

  const refresh = () => setTimeout(() => dispatch(masterSimList()), 600);

  // ------- create / edit -------
  const openModal = (item = null) => {
    if (item && item.Id) {
      setIsEditing(true);
      setEditId(item.Id);
      setFormData({
        Label: item.Label || "",
        Iccid: item.Iccid || "",
        Bic: item.Bic ? displayBic(item.Bic) : "",
        Imsi: item.Imsi || "",
        Msisdn: item.Msisdn || "",
        Notes: item.Notes || "",
      });
    } else {
      setIsEditing(false);
      setEditId(null);
      setFormData(initialFormState);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData(initialFormState);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    // Bic only goes up when it has a value or was changed, so this still
    // saves against a backend from before the Bic column existed
    const { Bic, ...rest } = formData;
    const original = isEditing ? sims.find((s) => s.Id === editId) : null;
    const bicChanged = isEditing
      ? cleanBic(Bic) !== (original?.Bic || "")
      : cleanBic(Bic) !== "";
    const payload = bicChanged ? { ...rest, Bic: cleanBic(Bic) } : rest;
    if (isEditing) {
      dispatch(masterSimUpdate([{ Id: editId, ...payload }]));
    } else {
      dispatch(masterSimCreate([{ ...payload }]));
    }
    closeModal();
    refresh();
  };

  const handleDeleteClick = (id) => {
    if (window.confirm("Remove this SIM from the inventory?")) {
      dispatch(masterSimDelete([id]));
      refresh();
    }
  };

  // ------- emnify actions -------
  const handleSync = () => {
    dispatch(masterSimSync());
    refresh();
  };

  // ------- inline label edit -------
  const startLabelEdit = (item) => {
    setLabelEditId(item.Id);
    setLabelDraft(item.Label || "");
  };

  const saveLabel = (item) => {
    if (labelEditId !== item.Id) return; // Enter then blur both fire
    setLabelEditId(null);
    const label = labelDraft.trim();
    if (label === (item.Label || "")) return;
    dispatch(masterSimUpdate([{ Id: item.Id, Label: label }]));
    refresh();
  };

  const openRegisterModal = () => {
    setRegisterForm(initialRegisterForm);
    setAssignStaff([]);
    setRegisterModalOpen(true);
  };

  const handleRegisterSubmit = () => {
    const bic = registerForm.Bic;
    if (!bic) {
      window.alert("Enter the BIC printed on the SIM pack.");
      return;
    }
    // The saga refreshes the grid once emnify has registered and synced
    dispatch(
      masterSimRegister([
        {
          Bic: bic,
          Activate: registerForm.Activate ? 1 : 0,
          // Optional extras only sent when filled, so an older backend never sees them
          ...(registerForm.Label.trim()
            ? { Label: registerForm.Label.trim() }
            : {}),
          ...(registerForm.Company_Id
            ? { Company_Id: registerForm.Company_Id }
            : {}),
          ...(registerForm.Company_Id && registerForm.Staff_Id
            ? { Staff_Id: registerForm.Staff_Id }
            : {}),
        },
      ])
    );
    setRegisterModalOpen(false);
  };

  const handleActivate = (item) => {
    dispatch(masterSimActivate([{ Sim_Id: item.Id }]));
    refresh();
  };

  const handleSuspend = (item) => {
    dispatch(masterSimSuspend([{ Sim_Id: item.Id }]));
    refresh();
  };

  // ------- make live (Change #100416) -------
  const openMakeLive = async (item) => {
    setLiveSim(item);
    setLiveForm({
      Name: item.Label || item.CurrentStaffName || "",
      ServiceProfile_Id: "",
      TariffProfile_Id: "",
      IpAddressSpace_Id: "",
      Imei: "",
      ImeiLock: false,
    });
    // Profiles come live from emnify; loaded once per page visit.
    let options = liveOptions;
    if (!options) {
      const res = await getMasterSimDeviceOptions();
      if (res?.status !== 200) {
        toast.error(res?.message || "Could not load the emnify profiles");
        setLiveSim(null);
        return;
      }
      options = res;
      setLiveOptions(res);
    }
    const d = options?.defaults || {};
    setLiveForm((prev) => ({
      ...prev,
      ServiceProfile_Id: d.service_profile_id
        ? String(d.service_profile_id)
        : "",
      TariffProfile_Id: d.tariff_profile_id ? String(d.tariff_profile_id) : "",
      IpAddressSpace_Id: d.ip_address_space_id
        ? String(d.ip_address_space_id)
        : "",
    }));
  };

  const handleMakeLive = async () => {
    if (!liveForm.ServiceProfile_Id || !liveForm.TariffProfile_Id) {
      toast.error("Choose a service profile and a tariff profile");
      return;
    }
    setLiveSaving(true);
    try {
      const res = await postMasterSimMakeLive([
        {
          Sim_Id: liveSim.Id,
          Name: liveForm.Name || "",
          ServiceProfile_Id: Number(liveForm.ServiceProfile_Id),
          TariffProfile_Id: Number(liveForm.TariffProfile_Id),
          ...(liveForm.IpAddressSpace_Id
            ? { IpAddressSpace_Id: Number(liveForm.IpAddressSpace_Id) }
            : {}),
          ...(liveForm.Imei
            ? { Imei: liveForm.Imei, ImeiLock: liveForm.ImeiLock ? 1 : 0 }
            : {}),
        },
      ]);
      if (res?.status === 200) {
        toast.success(res.message);
        setLiveSim(null);
      } else {
        toast.error(res?.message || "Could not make the SIM live");
      }
      refresh();
    } finally {
      setLiveSaving(false);
    }
  };

  // ------- assign / unassign -------
  const loadAssignStaff = async (companyId) => {
    setAssignStaff([]);
    if (!companyId) return;
    setIsStaffLoading(true);
    try {
      const response = await getMasterSimStaff(companyId);
      setAssignStaff(response?.staff_list || []);
    } catch (e) {
      setAssignStaff([]);
    }
    setIsStaffLoading(false);
  };

  // Also used on an assigned SIM to change the person (same company preselected)
  const openAssignModal = (item) => {
    const companyId = item.CurrentCompany_Id
      ? String(item.CurrentCompany_Id)
      : "";
    setAssignSim(item);
    setAssignForm({
      Company_Id: companyId,
      Staff_Id: item.CurrentStaff_Id ? String(item.CurrentStaff_Id) : "",
      Notes: "",
    });
    loadAssignStaff(companyId);
    setAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setAssignSim(null);
  };

  const handleAssignSubmit = () => {
    if (!assignForm.Company_Id) {
      window.alert("Please choose a company.");
      return;
    }
    dispatch(
      masterSimAssign([
        {
          Sim_Id: assignSim.Id,
          Company_Id: assignForm.Company_Id,
          // Only sent when chosen, so an older backend never sees the key
          ...(assignForm.Staff_Id ? { Staff_Id: assignForm.Staff_Id } : {}),
          Notes: assignForm.Notes,
        },
      ])
    );
    closeAssignModal();
    refresh();
  };

  const handleUnassign = (item) => {
    if (window.confirm(`Return SIM ${item.Iccid || item.Label} to stock?`)) {
      dispatch(masterSimUnassign([{ Sim_Id: item.Id }]));
      refresh();
    }
  };

  // ------- history -------
  const openHistory = (item) => {
    dispatch(masterSimData(item.Id));
    setHistoryModalOpen(true);
  };

  return (
    <React.Fragment>
      <ToastContainer position="top-right" autoClose={4000} closeOnClick />
      <SettingsPanel
        title="SIM Management"
        description="Central inventory of mobile-data SIMs (emnify). Sync the list from emnify, activate/suspend SIMs, and assign them to companies while keeping a full give/return history."
      >
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h4 className="card-title mb-0">emnify SIM Inventory</h4>
                  <div className="d-flex gap-2">
                    {/* Change #100010: straight to emnify's own portal, for the
                        things this screen doesn't do (usage, billing, SIM
                        settings). Its own login, so a new tab. */}
                    <Button
                      tag="a"
                      href="https://portal.emnify.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="light"
                      size="sm"
                      className="border"
                      title="Open the emnify portal in a new tab"
                    >
                      <i className="ri-external-link-line align-middle me-1"></i>
                      Open emnify portal
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      className="border"
                      onClick={() => dispatch(masterSimList())}
                      disabled={isLoading}
                    >
                      <i className="ri-refresh-line align-middle me-1"></i>
                      Refresh
                    </Button>
                    <Button
                      color="info"
                      size="sm"
                      onClick={handleSync}
                      disabled={isSyncing}
                    >
                      {isSyncing ? (
                        <Spinner size="sm" className="me-1" />
                      ) : (
                        <i className="ri-download-cloud-2-line align-middle me-1"></i>
                      )}
                      Sync from emnify
                    </Button>
                    <Button
                      color="success"
                      size="sm"
                      onClick={openRegisterModal}
                      disabled={isRegistering}
                      title="Register a new SIM batch with emnify using the BIC on the pack"
                    >
                      {isRegistering ? (
                        <Spinner size="sm" className="me-1" />
                      ) : (
                        <i className="ri-sim-card-2-line align-middle me-1"></i>
                      )}
                      Register in emnify
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => openModal()}
                      title="Record a SIM in CRMit only. Nothing is sent to emnify."
                    >
                      <i className="ri-add-line align-middle me-1"></i>
                      Add SIM manually
                    </Button>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 mb-3">
                  <div
                    className="search-box"
                    style={{ maxWidth: 360, flex: 1 }}
                  >
                    <Input
                      type="search"
                      bsSize="sm"
                      className="form-control search"
                      placeholder="Search label, ICCID, IMSI, MSISDN, company, person…"
                      value={simSearch}
                      onChange={(e) => setSimSearch(e.target.value)}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                  {simSearchWords.length > 0 && (
                    <span className="small text-muted">
                      {filteredSims.length} of {sims.length} SIMs
                    </span>
                  )}
                </div>

                <div className="table-responsive">
                  <Table className="table table-striped table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Label</th>
                        <th>ICCID</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Device</th>
                        <th>Assigned To</th>
                        <th>Last Synced</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-center text-muted py-4"
                          >
                            <Spinner size="sm" className="me-2" />
                            Loading SIMs...
                          </td>
                        </tr>
                      ) : sims.length > 0 && filteredSims.length === 0 ? (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-center text-muted py-4"
                          >
                            No SIMs match &quot;{simSearch.trim()}&quot;.
                          </td>
                        </tr>
                      ) : sims.length > 0 ? (
                        filteredSims.map((item) => {
                          const isAssigned = !!item.CurrentCompany_Id;
                          const type = simType(item);
                          return (
                            <tr
                              key={item.Id}
                              role="button"
                              onClick={() => setDetailSimId(item.Id)}
                            >
                              <td
                                className="fw-medium"
                                style={{ minWidth: 180 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {labelEditId === item.Id ? (
                                  <Input
                                    bsSize="sm"
                                    autoFocus
                                    value={labelDraft}
                                    placeholder="What is this SIM for?"
                                    onChange={(e) =>
                                      setLabelDraft(e.target.value)
                                    }
                                    onBlur={() => saveLabel(item)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") saveLabel(item);
                                      if (e.key === "Escape")
                                        setLabelEditId(null);
                                    }}
                                  />
                                ) : (
                                  <span
                                    role="button"
                                    title="Click to edit the label"
                                    onClick={() => startLabelEdit(item)}
                                  >
                                    {item.Label || (
                                      <span className="text-muted fw-normal">
                                        <i className="ri-pencil-line align-middle me-1"></i>
                                        Add label
                                      </span>
                                    )}
                                  </span>
                                )}
                              </td>
                              <td>
                                {item.Iccid ? (
                                  <code>{item.Iccid}</code>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                                {item.Bic && (
                                  <div
                                    className="small text-muted font-monospace"
                                    title="BIC the SIM was registered with"
                                  >
                                    BIC {displayBic(item.Bic)}
                                  </div>
                                )}
                              </td>
                              <td>
                                {/* Change #100663 - eSIM or physical SIM. */}
                                {type.kind === "esim" ? (
                                  <Badge color="info" title={type.detail}>
                                    <i className="ri-qr-code-line align-middle me-1"></i>
                                    eSIM
                                  </Badge>
                                ) : type.kind === "physical" ? (
                                  <Badge
                                    color="light"
                                    className="text-dark border"
                                    title={type.detail}
                                  >
                                    <i className="ri-sim-card-line align-middle me-1"></i>
                                    Physical SIM
                                  </Badge>
                                ) : (
                                  <span
                                    className="small text-muted"
                                    title="Not known yet - Sync from emnify fills this in"
                                  >
                                    —
                                  </span>
                                )}
                                {type.detail && (
                                  <div className="small text-muted">
                                    {type.detail}
                                  </div>
                                )}
                              </td>
                              <td>
                                <Badge color={statusColor(item.StatusId)}>
                                  {item.StatusDescription ||
                                    (item.StatusId != null
                                      ? item.StatusId
                                      : "Unknown")}
                                </Badge>
                              </td>
                              <td>
                                {/* Change #100416 - the emnify device (endpoint) the SIM is on. */}
                                {item.EmnifyEndpointId ? (
                                  <span
                                    className="small"
                                    title="emnify device (endpoint) id"
                                  >
                                    <i className="ri-device-line align-middle me-1"></i>
                                    #{item.EmnifyEndpointId}
                                  </span>
                                ) : (
                                  <span
                                    className="small text-danger"
                                    title="Not on a device - it passes no data until it is. Use Make live."
                                  >
                                    No device
                                  </span>
                                )}
                              </td>
                              <td>
                                {isAssigned ? (
                                  <>
                                    {item.CurrentCompanyName || (
                                      <span className="text-muted">
                                        Company #{item.CurrentCompany_Id}
                                      </span>
                                    )}
                                    {item.CurrentStaffName && (
                                      <div className="small text-muted">
                                        <i className="ri-user-line align-middle me-1"></i>
                                        {item.CurrentStaffName}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-muted">In stock</span>
                                )}
                              </td>
                              <td>
                                {item.LastSyncedDateTime || (
                                  <span className="text-muted">Never</span>
                                )}
                              </td>
                              <td className="text-end">
                                {/* Change #100665 - one button; every action
                                    is labelled inside the SIM modal. */}
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDetailSimId(item.Id);
                                  }}
                                  title="Open this SIM: live status, usage and actions"
                                >
                                  <i className="ri-settings-3-line align-middle me-1"></i>
                                  Manage
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-center text-muted py-4"
                          >
                            No SIMs yet. Click "Sync from emnify" to pull your
                            SIM list, or add one manually.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </SettingsPanel>

      {/* MAKE LIVE MODAL - Change #100416 */}
      {detailSim && (
        <SimDetailModal
          sim={detailSim}
          onClose={() => setDetailSimId(null)}
          onRefreshed={() => dispatch(masterSimList())}
          actions={{
            makeLive: openMakeLive,
            activate: handleActivate,
            suspend: handleSuspend,
            esimQr: setEsimQrSim,
            assign: openAssignModal,
            unassign: handleUnassign,
            history: openHistory,
            edit: openModal,
            remove: (item) => handleDeleteClick(item.Id),
          }}
        />
      )}

      {esimQrSim && (
        <EsimQrModal sim={esimQrSim} onClose={() => setEsimQrSim(null)} />
      )}

      <AppModal
        open={!!liveSim}
        onClose={() => setLiveSim(null)}
        title="Make SIM live"
        headerAccent="indigo"
        size="md"
        loading={liveSaving}
        loadingText="Talking to emnify..."
        onSubmit={handleMakeLive}
        submitLabel="Make live"
        submitColor="success"
      >
        <p className="text-muted small">
          A SIM only passes data once it is on an enabled emnify device. This
          {liveSim?.EmnifyEndpointId
            ? " enables the SIM's existing device"
            : " creates a device with the SIM attached"}
          , applies the profiles below, and activates the SIM if it is not
          already.
          {liveSim?.Iccid ? (
            <>
              {" "}
              ICCID <code>{liveSim.Iccid}</code>.
            </>
          ) : null}
        </p>
        {!liveOptions ? (
          <div className="text-center text-muted py-3">
            <Spinner size="sm" className="me-2" />
            Loading profiles from emnify...
          </div>
        ) : (
          <>
            <FormGroup className="mb-3">
              <Label htmlFor="LiveName">Device name</Label>
              <Input
                id="LiveName"
                value={liveForm.Name || ""}
                placeholder="e.g. WCB Derek iPad"
                onChange={(e) =>
                  setLiveForm((prev) => ({ ...prev, Name: e.target.value }))
                }
              />
              <small className="text-muted">
                Leave blank to use the SIM's label, person or ICCID.
              </small>
            </FormGroup>
            <FormGroup className="mb-3">
              <Label htmlFor="LiveService">Service profile</Label>
              <Input
                id="LiveService"
                type="select"
                value={liveForm.ServiceProfile_Id || ""}
                onChange={(e) =>
                  setLiveForm((prev) => ({
                    ...prev,
                    ServiceProfile_Id: e.target.value,
                  }))
                }
              >
                <option value="">Choose...</option>
                {(liveOptions.service_profiles || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.description ? ` - ${p.description}` : ""}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="mb-3">
              <Label htmlFor="LiveTariff">Tariff profile</Label>
              <Input
                id="LiveTariff"
                type="select"
                value={liveForm.TariffProfile_Id || ""}
                onChange={(e) =>
                  setLiveForm((prev) => ({
                    ...prev,
                    TariffProfile_Id: e.target.value,
                  }))
                }
              >
                <option value="">Choose...</option>
                {(liveOptions.tariff_profiles || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.description ? ` - ${p.description}` : ""}
                  </option>
                ))}
              </Input>
              <small className="text-muted">
                Pre-picked from your most recent enabled device.
              </small>
            </FormGroup>
            {!liveSim?.EmnifyEndpointId &&
              (liveOptions.ip_address_spaces || []).length > 1 && (
                <FormGroup className="mb-3">
                  <Label htmlFor="LiveIp">IP address space</Label>
                  <Input
                    id="LiveIp"
                    type="select"
                    value={liveForm.IpAddressSpace_Id || ""}
                    onChange={(e) =>
                      setLiveForm((prev) => ({
                        ...prev,
                        IpAddressSpace_Id: e.target.value,
                      }))
                    }
                  >
                    {(liveOptions.ip_address_spaces || []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              )}
            <FormGroup className="mb-2">
              <Label htmlFor="LiveImei">IMEI (optional)</Label>
              <Input
                id="LiveImei"
                value={liveForm.Imei || ""}
                placeholder="The device's IMEI, e.g. from Settings > General > About"
                onChange={(e) =>
                  setLiveForm((prev) => ({ ...prev, Imei: e.target.value }))
                }
              />
            </FormGroup>
            {liveForm.Imei ? (
              <FormGroup check className="mb-0">
                <Input
                  id="LiveImeiLock"
                  type="checkbox"
                  checked={!!liveForm.ImeiLock}
                  onChange={(e) =>
                    setLiveForm((prev) => ({
                      ...prev,
                      ImeiLock: e.target.checked,
                    }))
                  }
                />
                <Label check htmlFor="LiveImeiLock">
                  Lock the SIM to this IMEI (it will not work in any other
                  device)
                </Label>
              </FormGroup>
            ) : null}
          </>
        )}
      </AppModal>

      {/* CREATE / EDIT MODAL */}
      <AppModal
        open={isModalOpen}
        onClose={closeModal}
        title={isEditing ? "Edit SIM" : "Add SIM manually"}
        headerAccent="indigo"
        size="md"
        onSubmit={handleSubmit}
        submitLabel={isEditing ? "Save Changes" : "Create"}
        submitColor="primary"
      >
        {!isEditing && (
          <p className="text-muted small">
            This only records the SIM in CRMit. Nothing is sent to emnify. To
            register a new SIM pack with emnify, use Register in emnify instead.
          </p>
        )}
        <Row>
          <Col md={12}>
            <FormGroup className="mb-3">
              <Label htmlFor="Label">Label</Label>
              <Input
                id="Label"
                name="Label"
                value={formData.Label}
                onChange={handleInputChange}
                placeholder="e.g. Spare SIM #4"
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup className="mb-3">
              <Label htmlFor="Iccid">ICCID</Label>
              <Input
                id="Iccid"
                name="Iccid"
                value={formData.Iccid}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup className="mb-3">
              <Label htmlFor="EditBic">BIC</Label>
              <Input
                id="EditBic"
                name="Bic"
                value={formData.Bic}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    Bic: displayBic(cleanBic(e.target.value)),
                  }))
                }
                placeholder="#XXX-XXXX-XXXX-XXXX"
                autoComplete="off"
                spellCheck={false}
                className="font-monospace"
              />
              <small className="text-muted">
                Filled in automatically when registered through Register in
                emnify.
              </small>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="Imsi">IMSI</Label>
              <Input
                id="Imsi"
                name="Imsi"
                value={formData.Imsi}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="Msisdn">MSISDN</Label>
              <Input
                id="Msisdn"
                name="Msisdn"
                value={formData.Msisdn}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup className="mb-3">
              <Label htmlFor="Notes">Notes</Label>
              <Input
                type="textarea"
                id="Notes"
                name="Notes"
                rows={2}
                value={formData.Notes}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
      </AppModal>

      {/* REGISTER IN EMNIFY MODAL (Change #100343) */}
      <AppModal
        open={registerModalOpen}
        onClose={() => setRegisterModalOpen(false)}
        title="Register SIMs in emnify"
        headerAccent="indigo"
        size="md"
        onSubmit={handleRegisterSubmit}
        submitLabel="Register"
        submitColor="success"
      >
        <p className="text-muted small">
          Enter the 16-character BIC from the SIM pack label, including the #
          (e.g. #312-FBUD-2QO3-QNJI). The dashes are added for you, the same as
          the emnify portal. Every SIM in that pack is added to our emnify
          organisation, then synced into this list.
        </p>
        <FormGroup className="mb-3">
          <Label htmlFor="Bic">BIC</Label>
          <Input
            id="Bic"
            name="Bic"
            innerRef={bicInputRef}
            value={maskBic(registerForm.Bic)}
            onChange={(e) =>
              setRegisterForm((prev) => ({
                ...prev,
                Bic: cleanBic(e.target.value),
              }))
            }
            onFocus={keepBicCaret}
            onClick={keepBicCaret}
            onKeyUp={keepBicCaret}
            autoComplete="off"
            spellCheck={false}
            className="font-monospace"
            style={{ letterSpacing: "0.08em" }}
          />
          <small className="text-muted">e.g. #312-FBUD-2QO3-QNJI</small>
        </FormGroup>
        <FormGroup check className="mb-2">
          <Input
            type="checkbox"
            id="RegisterActivate"
            checked={registerForm.Activate}
            onChange={(e) =>
              setRegisterForm((prev) => ({
                ...prev,
                Activate: e.target.checked,
              }))
            }
          />
          <Label check htmlFor="RegisterActivate">
            Activate the SIMs straight away (otherwise they register as Issued)
          </Label>
        </FormGroup>

        <hr className="my-3" />
        <p className="text-muted small mb-2">
          Optional. Applied to every SIM in the pack.
        </p>
        <FormGroup className="mb-3">
          <Label htmlFor="RegisterLabel">Label</Label>
          <Input
            id="RegisterLabel"
            value={registerForm.Label}
            onChange={(e) =>
              setRegisterForm((prev) => ({ ...prev, Label: e.target.value }))
            }
            placeholder="What is this SIM for?"
          />
        </FormGroup>
        <Row>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="RegisterCompany_Id">Assign to company</Label>
              <Input
                type="select"
                id="RegisterCompany_Id"
                value={registerForm.Company_Id}
                onChange={(e) => {
                  const companyId = e.target.value;
                  setRegisterForm((prev) => ({
                    ...prev,
                    Company_Id: companyId,
                    Staff_Id: "",
                  }));
                  loadAssignStaff(companyId);
                }}
              >
                <option value="">Keep in stock</option>
                {companies.map((c) => (
                  <option key={c.Id} value={c.Id}>
                    {c.BusinessName} (#{c.Id})
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="RegisterStaff_Id">Person</Label>
              <Input
                type="select"
                id="RegisterStaff_Id"
                value={registerForm.Staff_Id}
                disabled={!registerForm.Company_Id || isStaffLoading}
                onChange={(e) =>
                  setRegisterForm((prev) => ({
                    ...prev,
                    Staff_Id: e.target.value,
                  }))
                }
              >
                <option value="">
                  {isStaffLoading
                    ? "Loading staff..."
                    : registerForm.Company_Id
                    ? "No specific person"
                    : "Choose a company first"}
                </option>
                {assignStaff.map((s) => (
                  <option key={s.Id} value={s.Id}>
                    {s.FullName ||
                      `${s.FirstName || ""} ${s.LastName || ""}`.trim() ||
                      `Staff #${s.Id}`}
                    {s.JobTitle ? ` (${s.JobTitle})` : ""}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>
      </AppModal>

      {/* ASSIGN MODAL */}
      <AppModal
        open={assignModalOpen}
        onClose={closeAssignModal}
        title={`Assign SIM ${assignSim?.Iccid || assignSim?.Label || ""}`}
        headerAccent="indigo"
        size="md"
        onSubmit={handleAssignSubmit}
        submitLabel="Assign"
        submitColor="primary"
      >
        <FormGroup className="mb-3">
          <Label htmlFor="Company_Id">Company</Label>
          <Input
            type="select"
            id="Company_Id"
            name="Company_Id"
            value={assignForm.Company_Id}
            onChange={(e) => {
              const companyId = e.target.value;
              setAssignForm((prev) => ({
                ...prev,
                Company_Id: companyId,
                Staff_Id: "",
              }));
              loadAssignStaff(companyId);
            }}
          >
            <option value="">Select a company...</option>
            {companies.map((c) => (
              <option key={c.Id} value={c.Id}>
                {c.BusinessName} (#{c.Id})
              </option>
            ))}
          </Input>
        </FormGroup>
        <FormGroup className="mb-3">
          <Label htmlFor="AssignStaff_Id">Person (optional)</Label>
          <Input
            type="select"
            id="AssignStaff_Id"
            name="Staff_Id"
            value={assignForm.Staff_Id}
            disabled={!assignForm.Company_Id || isStaffLoading}
            onChange={(e) =>
              setAssignForm((prev) => ({ ...prev, Staff_Id: e.target.value }))
            }
          >
            <option value="">
              {isStaffLoading
                ? "Loading staff..."
                : assignForm.Company_Id
                ? "No specific person"
                : "Choose a company first"}
            </option>
            {/* Keep a current holder who has since left, so it still shows */}
            {assignSim?.CurrentStaff_Id &&
              String(assignSim.CurrentCompany_Id) === assignForm.Company_Id &&
              !assignStaff.some(
                (s) => String(s.Id) === String(assignSim.CurrentStaff_Id)
              ) && (
                <option value={assignSim.CurrentStaff_Id}>
                  {assignSim.CurrentStaffName ||
                    `Staff #${assignSim.CurrentStaff_Id}`}
                </option>
              )}
            {assignStaff.map((s) => (
              <option key={s.Id} value={s.Id}>
                {s.FullName ||
                  `${s.FirstName || ""} ${s.LastName || ""}`.trim() ||
                  `Staff #${s.Id}`}
                {s.JobTitle ? ` (${s.JobTitle})` : ""}
              </option>
            ))}
          </Input>
        </FormGroup>
        <FormGroup className="mb-3">
          <Label htmlFor="AssignNotes">Notes</Label>
          <Input
            type="textarea"
            id="AssignNotes"
            name="Notes"
            rows={2}
            value={assignForm.Notes}
            onChange={(e) =>
              setAssignForm((prev) => ({ ...prev, Notes: e.target.value }))
            }
          />
        </FormGroup>
      </AppModal>

      {/* HISTORY MODAL */}
      <AppModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title="Assignment History"
        headerAccent="indigo"
        size="lg"
        hideFooter
      >
        {isHistoryLoading ? (
          <div className="text-center text-muted py-4">
            <Spinner size="sm" className="me-2" />
            Loading history...
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Company</th>
                  <th>Person</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {historySim?.assignments?.length > 0 ? (
                  historySim.assignments.map((a) => (
                    <tr key={a.Id}>
                      <td>{a.CompanyName || `Company #${a.Company_Id}`}</td>
                      <td>
                        {a.StaffName || <span className="text-muted">—</span>}
                      </td>
                      <td>{a.AssignedStartDateTime || "—"}</td>
                      <td>
                        {a.AssignedEndDateTime || (
                          <Badge color="success">Current</Badge>
                        )}
                      </td>
                      <td>
                        {a.Notes || <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-3">
                      No assignment history for this SIM.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </AppModal>
    </React.Fragment>
  );
};

export default MasterSim;
